/**
 * BprDiagramsSection - the engagement's visual output layer.
 *
 * BPR output as a navigable section list: System Architecture is ALWAYS the
 * first, pinned section (with a current/future state toggle); every
 * functional area follows with its workflow diagram. Sections render from
 * the newest cached spec batch; Generate/Regenerate runs the diagram skill
 * over the engagement's latest knowledge graph. Clicking an annotated node
 * opens its linked issues in the side panel.
 */

import type { BprDiagramNode, BprDiagramRecord, BprDiagramState } from '@protoss/shared';
import { Button, Pill } from '@protoss/ui';
import * as React from 'react';
import { bprApi, type BprGraphDiagramResult } from '../services/bprApi';
import { diagramErrorMessage } from '../services/dealDiagramsStore';
import { BprDiagramCanvas } from './BprDiagramCanvas';
import { BprDiagramLegend } from './BprDiagramLegend';
import { BprGraphPriorityView } from './BprGraphPriorityView';
import { BprGraphSystemView } from './BprGraphSystemView';

const ARCHITECTURE_SECTION = '__architecture__';

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface BprDiagramsSectionProps {
  engagementUuid: string;
}

export function BprDiagramsSection({ engagementUuid }: BprDiagramsSectionProps) {
  const [diagrams, setDiagrams] = React.useState<BprDiagramRecord[] | null>(null);
  // The engagement's latest knowledge-graph diagram; its priority_view (the
  // inverted triangle) renders under the System Architecture section.
  const [graphDiagram, setGraphDiagram] = React.useState<BprGraphDiagramResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [section, setSection] = React.useState<string>(ARCHITECTURE_SECTION);
  // One current/future toggle shared by the architecture pair AND a section's
  // workflow pair; reset to current on section change.
  const [stateTab, setStateTab] = React.useState<BprDiagramState>('current');
  const [selectedNode, setSelectedNode] = React.useState<BprDiagramNode | null>(null);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [batch, graph] = await Promise.all([
        bprApi.getDiagrams(engagementUuid),
        bprApi.getGraphDiagram(engagementUuid),
      ]);
      setDiagrams(batch);
      setGraphDiagram(graph);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load diagrams.');
    } finally {
      setIsLoading(false);
    }
  }, [engagementUuid]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const handleGenerate = async (force: boolean) => {
    setIsGenerating(true);
    setError(null);
    try {
      // A fresh generation is an async run server-side; bprApi polls it to
      // completion, so this await still spans the whole generation.
      const result = await bprApi.generateDiagrams(engagementUuid, force);
      setDiagrams(result.diagrams);
      setSection(ARCHITECTURE_SECTION);
      setSelectedNode(null);
      if (result.failedSectionKeys.length > 0) {
        setError(diagramErrorMessage(new Error('BPR_DIAGRAM_GENERATION_INVALID_OUTPUT')));
      }
    } catch (err) {
      setError(diagramErrorMessage(err));
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return <p className="bpr-section__empty">Loading diagrams…</p>;
  }

  const architecture = (diagrams ?? []).filter((d) => d.kind === 'system-architecture');
  const workflows = (diagrams ?? []).filter((d) => d.kind === 'workflow');

  if (!diagrams || diagrams.length === 0) {
    return (
      <div className="bpr-section__empty bpr-diagrams__unbuilt">
        <p>
          No diagrams generated yet. Build the knowledge graph first, then generate the visual
          output: the system architecture (current + future state) and a workflow diagram per
          functional area.
        </p>
        {error ? <p className="bpr-dialog__error">{error}</p> : null}
        <Button
          type="button"
          size="sm"
          disabled={isGenerating}
          onClick={() => void handleGenerate(false)}
        >
          {isGenerating ? 'Generating… (a few minutes)' : 'Generate diagrams'}
        </Button>
      </div>
    );
  }

  // Workflows come as current/future pairs per section key (legacy batches
  // carry one null-state entry = current). The nav lists each section once.
  const workflowNav = workflows.filter((d) => d.state !== 'future');
  const sectionCurrent =
    workflowNav.find((d) => (d.functionalAreaKey ?? d.uuid) === section) ?? null;
  const sectionFuture =
    workflows.find((d) => d.state === 'future' && (d.functionalAreaKey ?? d.uuid) === section) ??
    null;

  const activeArchitecture =
    architecture.find((d) => d.state === stateTab) ?? architecture[0] ?? null;
  const activeWorkflow = stateTab === 'future' && sectionFuture ? sectionFuture : sectionCurrent;
  const activeDiagram = section === ARCHITECTURE_SECTION ? activeArchitecture : activeWorkflow;
  // The toggle shows wherever the active section has both states.
  const showStateToggle =
    section === ARCHITECTURE_SECTION ? architecture.length > 1 : sectionFuture !== null;
  const generatedAt = diagrams[0]?.generatedAt;

  return (
    <div className="bpr-diagrams">
      <div className="bpr-section__toolbar">
        <h2 className="bpr-section__heading">Diagrams</h2>
        <span className="bpr-diagrams__meta">
          <Pill variant="ghost">
            {workflowNav.length} workflow{workflowNav.length === 1 ? '' : 's'}
          </Pill>
          <span className="bpr-diagrams__generated-at">
            generated {formatDateTime(generatedAt)}
          </span>
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isGenerating}
          onClick={() => void handleGenerate(true)}
        >
          {isGenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </div>
      {error ? <p className="bpr-dialog__error">{error}</p> : null}

      <div className="bpr-diagrams__body">
        {/* Section nav: architecture pinned first, then one entry per area. */}
        <nav className="bpr-diagrams__nav" aria-label="Diagram sections">
          <button
            type="button"
            className={`bpr-diagrams__nav-item${
              section === ARCHITECTURE_SECTION ? ' bpr-diagrams__nav-item--active' : ''
            } bpr-diagrams__nav-item--pinned`}
            onClick={() => {
              setSection(ARCHITECTURE_SECTION);
              setStateTab('current');
              setSelectedNode(null);
            }}
          >
            System Architecture
          </button>
          {workflowNav.map((d) => {
            const key = d.functionalAreaKey ?? d.uuid;
            return (
              <button
                key={key}
                type="button"
                className={`bpr-diagrams__nav-item${
                  section === key ? ' bpr-diagrams__nav-item--active' : ''
                }`}
                onClick={() => {
                  setSection(key);
                  setStateTab('current');
                  setSelectedNode(null);
                }}
              >
                {d.title}
              </button>
            );
          })}
        </nav>

        <div className="bpr-diagrams__stage">
          <div className="bpr-diagrams__stage-head">
            <h3 className="bpr-section__subheading">{activeDiagram?.title ?? 'Diagram'}</h3>
            {showStateToggle ? (
              <div className="bpr-diagrams__state-toggle" role="tablist" aria-label="Diagram state">
                {(['current', 'future'] as const).map((state) => (
                  <button
                    key={state}
                    type="button"
                    role="tab"
                    aria-selected={stateTab === state}
                    className={`bpr-diagrams__state-btn${
                      stateTab === state ? ' bpr-diagrams__state-btn--active' : ''
                    }`}
                    onClick={() => {
                      setStateTab(state);
                      setSelectedNode(null);
                    }}
                  >
                    {state === 'current' ? 'Current state' : 'Future state'}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="bpr-diagrams__canvas-row">
            {activeDiagram ? (
              <BprDiagramCanvas
                key={activeDiagram.uuid + stateTab}
                spec={activeDiagram.spec}
                onSelectNode={setSelectedNode}
                exportTitle={activeDiagram.title}
              />
            ) : (
              <p className="bpr-section__empty">No diagram for this section.</p>
            )}

            {selectedNode ? (
              <aside className="bpr-diagrams__issues" aria-label="Node issues">
                <header className="bpr-diagrams__issues-head">
                  <span className="bpr-diagrams__issues-title">{selectedNode.label}</span>
                  <Pill variant="ghost">{selectedNode.system}</Pill>
                  <Pill variant="neutral">{selectedNode.status}</Pill>
                </header>
                {selectedNode.issues.length === 0 ? (
                  <p className="bpr-diagrams__issues-empty">No issues linked to this node.</p>
                ) : (
                  <ul className="bpr-diagrams__issues-list">
                    {selectedNode.issues.map((issue, idx) => (
                      <li key={idx} className="bpr-diagrams__issue">
                        {issue}
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
            ) : null}
          </div>

          <BprDiagramLegend hint="click a node to see its linked issues" />

          {/* Under System Architecture: the knowledge graph's inverted
              triangle (prioritized findings) for the same engagement. */}
          {section === ARCHITECTURE_SECTION && graphDiagram ? (
            <>
              {graphDiagram.system_view.nodes.length > 0 ? (
                <section className="bpr-graph__panel bpr-diagrams__triangle-panel">
                  <h3 className="bpr-section__subheading">System map</h3>
                  <p className="bpr-graph__hint">
                    Layer 1 of the triangle: the systems the client runs and the integration points
                    between them. Dashed red edges are integration gaps; badges count the findings
                    on a system.
                  </p>
                  <BprGraphSystemView
                    systemView={graphDiagram.system_view}
                    exportTitle="System map"
                  />
                </section>
              ) : null}
              {graphDiagram.priority_view.items.length > 0 ? (
                <section className="bpr-graph__panel bpr-diagrams__triangle-panel">
                  <h3 className="bpr-section__subheading">Prioritized findings</h3>
                  <p className="bpr-graph__hint">
                    The knowledge graph's inverted triangle: findings bucketed into deterministic
                    Critical / High / Medium / Low tiers (apex first).
                  </p>
                  <BprGraphPriorityView priorityView={graphDiagram.priority_view} />
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
